import { ACQUISITION_COST_MIN_MAX, PRICE_PER_BARREL_MIN_MAX } from '../constants';
import {
  LOCAL_STORAGE_KEY_ACQUISITION_COST,
  LOCAL_STORAGE_KEY_PRICE_BARREL,
} from '../../../../../utils/constants';
import {
  roundDownToNearestThousand,
  roundToNearest,
  roundUpToNearestThousand,
} from '../../../../../utils/helpers';

import { useGetFiltersSummary } from './useGetFiltersSummary';
import { useMemo } from 'react';

const getLocalStorage = (key) => {
  try {
    return localStorage.getItem(key) ? JSON.parse(localStorage.getItem(key)) : null;
  } catch (e) {
    return null;
  }
};

function getValue({ config, defaultMin, defaultMax, persisted }) {
  return {
    ...config,
    ...{
      min: {
        ...config.min,
        default: defaultMin,
        initialValue: (persisted ? persisted[config.min.key] : defaultMin) || defaultMin,
      },
      max: {
        ...config.max,
        default: defaultMax,
        initialValue: (persisted ? persisted[config.max.key] : defaultMax) || defaultMax,
      },
    },
  };
}

export const useGetMinMaxFilters = () => {
  const { data, loading } = useGetFiltersSummary();

  const acquisitionCostPersisted = useMemo(
    () => getLocalStorage(LOCAL_STORAGE_KEY_ACQUISITION_COST),
    [],
  );
  const priceBarrelPersisted = useMemo(() => getLocalStorage(LOCAL_STORAGE_KEY_PRICE_BARREL), []);

  const { priceFlowingBarrel, acquisitionCost } = useMemo(() => {
    if (!data || loading) {
      return {
        priceFlowingBarrel: getValue({
          config: PRICE_PER_BARREL_MIN_MAX,
          defaultMin: PRICE_PER_BARREL_MIN_MAX.min.default,
          defaultMax: PRICE_PER_BARREL_MIN_MAX.max.default,
          persisted: priceBarrelPersisted,
        }),
        acquisitionCost: getValue({
          config: ACQUISITION_COST_MIN_MAX,
          defaultMin: ACQUISITION_COST_MIN_MAX.min.default,
          defaultMax: ACQUISITION_COST_MIN_MAX.max.default,
          persisted: acquisitionCostPersisted,
        }),
      };
    }

    const priceBarrelMinDefault = roundDownToNearestThousand(data.minPriceFlowingBarrelOutliers);
    const priceBarrelMaxDefault = roundUpToNearestThousand(data.maxPriceFlowingBarrelOutliers);
    const acquisitionCostMinDefault = roundToNearest(data.minAcquisitionCostOutliers, 10, false);
    const acquisitionCostMaxDefault = roundToNearest(data.maxAcquisitionCostOutliers, 10, true);

    return {
      priceFlowingBarrel: getValue({
        config: PRICE_PER_BARREL_MIN_MAX,
        defaultMin: priceBarrelMinDefault,
        defaultMax: priceBarrelMaxDefault,
        persisted: priceBarrelPersisted,
      }),
      acquisitionCost: getValue({
        config: ACQUISITION_COST_MIN_MAX,
        defaultMin: acquisitionCostMinDefault,
        defaultMax: acquisitionCostMaxDefault,
        persisted: acquisitionCostPersisted,
      }),
    };
  }, [data, loading]);

  return {
    acquisitionCost: acquisitionCost,
    priceFlowingBarrel: priceFlowingBarrel,
    loading,
  };
};
